import { useListCategories } from '@/hooks/useListCategories';
import useModalStore from '@/store/useModalStore';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CustomSelect from '../shared/CustomSelect/CustomSelect';
import { MultiComboBox } from '../shared/MultiComboBox/MultiComboBox';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { DualRangeSlider } from '../ui/dual-range-slider';
import { Label } from '../ui/label';
import { SheetFooter, SheetHeader, SheetTitle } from '../ui/sheet';

interface FilterFormData {
  category_type: string;
  commission_range: number[];
  currency: string;
  category_id: string[];
  price: number[];
}

interface MarketFilterProps {
  onFilter?: (data: FilterFormData) => void;
  filterParams?: Partial<FilterFormData>;
}

export default function MarketFilter({
  onFilter,
  filterParams,
}: MarketFilterProps) {
  const { t } = useTranslation(['common', 'sales']);
  const { closeModal } = useModalStore();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm({
    shouldUnregister: false,
    // resolver: zodResolver(FilterSchema),
    defaultValues: {
      category_type: filterParams?.category_type || 'digital',
      commission_range: filterParams?.commission_range || [0, 100],
      currency: filterParams?.currency || '',
      category_id: filterParams?.category_id || [],
      price: filterParams?.price || [0, 3000],
    },
  });

  const formValues = watch();

  const categoryType = formValues?.category_type || 'digital';

  useEffect(() => {
    if (!formValues?.category_type) {
      setValue('category_type', 'digital');
    }
  }, [formValues?.category_type, setValue]);

  const { categories = [] } = useListCategories({
    categoryType: categoryType,
  });

  const currencyOptions = [
    { value: 'BRL', label: t('brl') },
    { value: 'USD', label: t('usd') },
    { value: 'EUR', label: t('eur') },
  ];

  const filterCategories = async (query: string): Promise<Array<{ value: string; label: string }>> => {
    if (!categories || categories.length === 0) {
      return [];
    }
    return categories.filter((category) =>
      category.label.toLowerCase().includes(query.toLowerCase())
    );
  };

  const categoryTypeOptions = [
    { value: 'digital', label: t('common:digital') },
    { value: 'pharmaceutical', label: t('common:pharmaceutical') },
  ];

  const onSubmit = (data: FilterFormData) => {
    const commissionRangeAsString = data.commission_range.map((v: number) =>
      String(v)
    );
    onFilter?.({ ...data, commission_range: commissionRangeAsString as unknown as number[] });
    closeModal('market-filter');
  };

  const handleCategoryTypeChange = (value: string) => {
    setValue('category_type', value);
    setValue('category_id', []);
    clearErrors('category_id');
  };

  return (
    <div className='h-full flex relative flex-col justify-between'>
      <div className='h-full overflow-y-auto'>
        <SheetHeader className='md:p-6 p-4'>
          <SheetTitle className='flex gap-2'>{t('sales:filters')}</SheetTitle>
        </SheetHeader>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className='space-y-4 p-4 flex flex-col justify-between h-full'
        >
          <div className='flex flex-col gap-4'>
            <CustomSelect
              label={t('format')}
              placeholder={t('formatPlaceholder')}
              value={formValues.category_type}
              options={categoryTypeOptions}
              onValueChange={handleCategoryTypeChange}
            />
            <MultiComboBox
              label={t('common:category')}
              placeholder={t('common:categoryPlaceholder')}
              fetchOptions={filterCategories}
              onChange={(value) => {
                setValue('category_id', value);
                clearErrors('category_id');
              }}
              values={formValues.category_id}
              queryKey='market-filter-category'
              newParams={[categoryType]}
            />

            <CustomSelect
              label={t('currency')}
              placeholder={t('currencyPlaceholder')}
              value={formValues.currency}
              options={currencyOptions}
              onValueChange={(value) => setValue('currency', value)}
            />

            <div className='w-full space-y-5'>
              <Label className='mb-8'>{t('common:commission_value')}</Label>
              <DualRangeSlider
                labelPosition='bottom'
                label={(value) => (
                  <Badge variant='secondary' className='mt-2'>
                    {value}%
                  </Badge>
                )}
                value={formValues.commission_range}
                onValueChange={(value) => setValue('commission_range', value)}
                min={0}
                max={100}
                step={5}
                style={{
                  marginTop: 16,
                  paddingLeft: 4,
                  paddingRight: 4,
                }}
              />
            </div>

            <div className='w-full mt-10 mb-6'>
              <Label className='mb-8'>
                {t('common:product_value')}{' '}
                {formValues?.currency && `(${formValues.currency})`}
              </Label>
              <DualRangeSlider
                labelPosition='bottom'
                label={(value) => (
                  <Badge variant='secondary' className='mt-2 flex'>
                    {value}
                  </Badge>
                )}
                value={formValues.price}
                onValueChange={(value) => setValue('price', value)}
                min={0}
                max={3000}
                step={50}
                style={{
                  marginTop: 16,
                }}
              />
            </div>
          </div>

          <SheetFooter className='flex sticky z-50 bottom-0 bg-[#09090b] w-full border-t border-t-colorBorder p-4 items-end mt-4 justify-end'>
            <div className='flex w-full z-50 gap-2 justify-end items-end'>
              <Button
                variant='ghost'
                className='w-full !text-colorText md:max-w-[118px]'
                onClick={() => closeModal('market-filter')}
                type='button'
              >
                {t('cancel')}
              </Button>
              <Button
                variant='default'
                className='w-full md:max-w-fit'
                type='submit'
                data-cy='apply-filters'
              >
                {t('sales:apply_filters')}
              </Button>
            </div>
          </SheetFooter>
        </form>
      </div>
    </div>
  );
}
